import React from "react";
import "./StandardsPulseReport.css";
import apiClient from "../../../actions/api-client";
import { connect } from "react-redux";
import Heading from "../../heading/heading";
import { browserHistory } from "react-router";
import { Modal } from "react-bootstrap";
import SelectFieldGroup from "../../forms/select-field-group";

/**
 * StandardsPulseReport
 * ---------------------------------------------------------------------
 * Renders the "Standards Pulse" panel driven directly by the response
 * shape returned by the report lambda (getStudentReportForTeacherAndSubject
 * / getPulseReportForTeacherAndSubject):
 *
 *   {
 *     assignment: [ { category, answeredCorrectly, answeredIncorrectly,
 *                      totalQuestions, average, subCategories: [
 *                        { category, answeredCorrectly, answeredIncorrectly,
 *                          totalQuestions, average,
 *                          students: [ { studentID, studentName,
 *                                        answeredCorrectly, answeredIncorrectly,
 *                                        totalQuestions, average } ] }
 *                      ] } ],
 *     practiceAssignment: [ ...same shape... ],
 *     cumulative: [ ...same shape... ],
 *     studentsList: [ ... ]
 *   }
 *
 * Each subCategory becomes one "standard" row. The `students` array on the
 * subCategory (added when we extended the lambda) feeds the "below
 * threshold" count and hover popover.
 *
 * SELF-FETCHING MODE (recommended — matches your existing API call pattern):
 *   <StandardsPulseReport
 *     apiClient={apiClient}                // your generated AWS API Gateway client
 *     auth={this.props.auth}               // redux auth prop, needs auth.user.userName
 *     subject={selectedSubject}
 *     teacherID={optionalTeacherOverride}  // omit to use auth.user.userName
 *     section="cumulative"
 *     onFullReport={() => history.push("/reports/pulse")}
 *     onSuggestRemediation={(belowStandards) => openRemediationModal(belowStandards)}
 *     onViewAllStudents={(standard) => openStudentDrawer(standard)}
 *   />
 *
 * CONTROLLED MODE (pass the response in yourself, e.g. if a parent screen
 * already fetches it, or for storybook/tests — fetching is skipped):
 *   <StandardsPulseReport apiResponse={apiResponse} section="cumulative" />
 */
class StandardsPulseReport extends React.Component {
  numberOfStudentsInPopover = 2;
  static defaultProps = {
    section: "assignment",
    atRiskMax: 70, // <= this average is "At Risk" (0–70%, also the "below threshold" cutoff)
    approachingMax: 81, // Approaching is 71–80%; Proficient starts at this value
    proficientMax: 91, // Proficient is 81–90%; Mastered starts at this value (91–100%)
    standardNames: {}, // optional { [standardCode]: "Human readable description" }
    onFullReport: null,
    onSuggestRemediation: null,
    apiResponse: null, // pass this to run in controlled mode (skips fetching)
    auth: null, // redux auth prop, needs auth.user.userName
    teacherID: null, // optional override; falls back to auth.user.userName
    subject: null, // required for self-fetching mode
    maxVisibleRows: null, // optional. If a number > 0, only that many rows
    // (across "needs action" + "on track" combined, in the current tier
    // filter) are shown initially, with a "Show more" toggle to reveal the
    // rest. If omitted, every row is always shown — the tier filter bar is
    // then the only thing that narrows what's visible.
  };

  constructor(props) {
    super(props);
    this.state = {
      filter: "all", // "all" | "atrisk" | "approaching" | "proficient" | "mastered"
      expanded: false, // when maxVisibleRows truncates the list, whether the rest is revealed
      loading: false,
      error: null,
      apiResponse: null,
      showAlert: false,
      alertStyle: "",
      alertText: "",
      pinnedCode: null, // code of the standard whose popover is pinned open via click
      pinnedStyle: {}, // computed { top, left, width } for the pinned popover
      hoveredCode: null, // code of the standard whose popover is showing via hover/focus
      hoveredStyle: {}, // computed { top, left, width } for the hovered popover
      categoryView: false, // false = merged subcategory-only rows (buildStandardsForSubCategories);
      // true = raw per-category rows (buildStandardsWithCategories)
    };
    this.setFilter = this.setFilter.bind(this);
    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.fetchPulseReport = this.fetchPulseReport.bind(this);
    this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
    this.handlePopoverClose = this.handlePopoverClose.bind(this);
    this.handlePopoverToggle = this.handlePopoverToggle.bind(this);
    this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);
    this.onViewAllStudents = this.onViewAllStudents.bind(this);
    this.getSubjectSelections = this.getSubjectSelections.bind(this);
    this.getAllSubjects = this.getAllSubjects.bind(this);
    this.toggleCategoryView = this.toggleCategoryView.bind(this);
    this._alertTimeout = null;
    this._hoverCloseTimeout = null;
  }

  componentDidMount() {
    this.getAllSubjects();
    this.fetchPulseReport();
    document.addEventListener("mousedown", this.handleDocumentMouseDown);
  }

  componentDidUpdate(prevProps) {
    const prevSubject = prevProps.params && prevProps.params.subject;
    const nextSubject = this.props.params && this.props.params.subject;
    const prevUserName =
      prevProps.auth && prevProps.auth.user && prevProps.auth.user.userName;
    const nextUserName =
      this.props.auth && this.props.auth.user && this.props.auth.user.userName;

    if (prevSubject !== nextSubject || prevUserName !== nextUserName) {
      // New subject (or teacher) — drop any stale filter/expansion/popover
      // state from the previous subject's data before fetching the new one.
      this.setState({
        filter: "all",
        expanded: false,
        pinnedCode: null,
        hoveredCode: null,
      });
      this.fetchPulseReport();
    }
  }

  componentWillUnmount() {
    if (this._alertTimeout) {
      clearTimeout(this._alertTimeout);
    }
    if (this._hoverCloseTimeout) {
      clearTimeout(this._hoverCloseTimeout);
    }
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
  }

  /**
   * Fetches the pulse report using the same apiClient.invokeApi pattern used
   * elsewhere in the app. Mirrors your existing call: params { subject,
   * userName }, GET /assignment/analysis/pulse/subject/{subject}/teacher/{userName}.
   */
  fetchPulseReport() {
    const subject = this.props.params.subject;
    const context = this;
    const auth = this.props.auth;
    if (!subject) {
      console.error(
        "StandardsPulseReport: subject prop is required for self-fetching mode.",
      );
      return;
    }

    const userName = auth && auth.user && auth.user.userName;

    this.setState({ loading: true, error: null });

    return new Promise((resolve, reject) => {
      const params = {
        subject: subject,
        userName: userName,
      };
      const pathTemplate =
        "/assignment/analysis/pulse/subject/{subject}/teacher/{userName}";
      const method = "GET";
      const additionalParams = {};
      const body = {};

      apiClient
        .invokeApi(params, pathTemplate, method, additionalParams, body, "4")
        .then(function (result) {
          context.setState({ loading: false, apiResponse: result.data });
          resolve(result.data);
        })
        .catch(function (error) {
          context.setState({
            loading: false,
            error: error,
            showAlert: true,
            alertStyle: "danger",
            alertText: "Unable to load data, please contact administrator.",
          });
          if (context._alertTimeout) {
            clearTimeout(context._alertTimeout);
          }
          context._alertTimeout = setTimeout(() => {
            context.setState({
              showAlert: false,
              alertStyle: "",
              alertText: "",
            });
          }, 4000);
          reject(error);
        });
    });
  }

  setFilter(tierKey) {
    this.setState({
      filter: tierKey,
      expanded: false,
    });
  }

  toggleExpanded() {
    this.setState((prev) => ({ expanded: !prev.expanded }));
  }

  toggleCategoryView() {
    this.setState((prev) => ({
      categoryView: !prev.categoryView,
      filter: "all",
      expanded: false,
      pinnedCode: null,
      hoveredCode: null,
    }));
  }

  /**
   * Rough height of the popover for a given number of below-threshold
   * students, matching the CSS (header ~46px, list rows ~28px each capped
   * at the 220px max-height, footer ~34px). Used to decide whether it fits
   * below the trigger or needs to flip upward instead.
   */
  estimatePopoverHeight(studentCount) {
    const headerHeight = 46;
    const footerHeight = studentCount > this.numberOfStudentsInPopover ? 34 : 0;
    const itemHeight = 28;
    const listHeight = Math.min(
      Math.min(studentCount, this.numberOfStudentsInPopover) * itemHeight,
      220,
    );
    return headerHeight + listHeight + footerHeight;
  }

  /**
   * Computes viewport-relative { top, left, width } for the popover based on
   * the trigger's current position, flipping it above the trigger when
   * there isn't room below, and clamping horizontally so it never runs
   * off-screen. Shared by both the hover-open and click-pin paths.
   */
  computePopoverPosition(rect, standard, right) {
    const popoverWidth = 200;
    const margin = 8;
    const estimatedHeight = this.estimatePopoverHeight(
      standard.belowStudents.length,
    );
    right = document.documentElement.clientWidth - right;

    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < estimatedHeight + margin;
    const top = flipUp
      ? Math.max(margin, rect.top - estimatedHeight - 25)
      : rect.bottom + 5;

    return { top, right };
  }

  /**
   * Hover/focus open. Independent of any pinned popover — hovering a
   * different row shows its popover alongside a pinned one. If this exact
   * row is already pinned, there's nothing extra to do. Cancels any
   * pending close timeout from a previously hovered row.
   */
  handlePopoverOpen(event, standard) {
    if (!standard.belowStudents || standard.belowStudents.length === 0) return;
    if (this.state.pinnedCode === standard.code) return;

    if (this._hoverCloseTimeout) {
      clearTimeout(this._hoverCloseTimeout);
      this._hoverCloseTimeout = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    this.setState({
      hoveredCode: standard.code,
      hoveredStyle: this.computePopoverPosition(
        rect,
        standard,
        event.currentTarget.getBoundingClientRect().right,
      ),
    });
  }

  /**
   * Hover/focus close — doesn't clear immediately. Gives the user a short
   * grace period to move the mouse from the trigger cell into the popover
   * itself (which cancels this timeout via onMouseEnter, see
   * renderPopover) before it actually closes. A pinned popover is
   * untouched either way — it only closes via handlePopoverToggle
   * (clicking the same cell again) or an outside click.
   */
  handlePopoverClose() {
    if (this._hoverCloseTimeout) {
      clearTimeout(this._hoverCloseTimeout);
    }
    this._hoverCloseTimeout = setTimeout(() => {
      this.setState({ hoveredCode: null });
      this._hoverCloseTimeout = null;
    }, 300);
  }

  /**
   * Click on a "below threshold" cell: pins that popover open so it
   * survives the mouse leaving. Clicking the same cell again (while
   * pinned) closes it; clicking a different cell re-pins to that one
   * instead. Pinning doesn't prevent other rows from still showing their
   * own popover on hover.
   */
  handlePopoverToggle(event, standard) {
    event.stopPropagation();
    if (!standard.belowStudents || standard.belowStudents.length === 0) return;

    if (this.state.pinnedCode === standard.code) {
      this.setState({ pinnedCode: null });
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    this.setState({
      pinnedCode: standard.code,
      pinnedStyle: this.computePopoverPosition(
        rect,
        standard,
        event.currentTarget.getBoundingClientRect().right,
      ),
      hoveredCode: null,
    });
  }

  /**
   * Closes a pinned popover when the user clicks anywhere outside its
   * trigger cell or the popover itself.
   */
  handleDocumentMouseDown(event) {
    if (!this.state.pinnedCode) return;
    const target = event.target;
    if (
      target.closest &&
      (target.closest(".sp-stu-cell") || target.closest(".sp-popover"))
    ) {
      return;
    }
    this.setState({ pinnedCode: null });
  }

  tier(average) {
    const { atRiskMax, approachingMax, proficientMax } = this.props;
    if (average >= proficientMax) {
      return {
        key: "mastered",
        label: "Mastered",
        nc: "sp-ms-n",
        pc: "sp-ms-pill",
        bc: "sp-ms-bar",
      };
    }
    if (average >= approachingMax) {
      return {
        key: "proficient",
        label: "Proficient",
        nc: "sp-pr-n",
        pc: "sp-pr-pill",
        bc: "sp-pr-bar",
      };
    }
    if (average > atRiskMax) {
      return {
        key: "approaching",
        label: "Approaching",
        nc: "sp-ap-n",
        pc: "sp-ap-pill",
        bc: "sp-ap-bar",
      };
    }
    return {
      key: "atrisk",
      label: "At Risk",
      nc: "sp-ar-n",
      pc: "sp-ar-pill",
      bc: "sp-ar-bar",
    };
  }

  /**
   * Range + plain-language description for each tier, computed from the
   * configured thresholds so the displayed numbers always match the actual
   * cutoffs even if atRiskMax/approachingMax/proficientMax are overridden.
   * Feeds both the always-visible range label under each tier button and
   * the hover detail popover.
   */
  tierInfo() {
    const { atRiskMax, approachingMax, proficientMax } = this.props;
    return {
      all: {
        range: "0–100%",
        description: "Every standard, regardless of tier.",
      },
      atrisk: {
        range: `0–${atRiskMax}%`,
        description: "Failing or on the edge of failing — needs intervention now.",
      },
      approaching: {
        range: `${atRiskMax + 1}–${approachingMax - 1}%`,
        description: "Passing but not solid — needs continued practice.",
      },
      proficient: {
        range: `${approachingMax}–${proficientMax - 1}%`,
        description: "Meeting expectations consistently.",
      },
      mastered: {
        range: `${proficientMax}–100%`,
        description: "Owns the standard — can apply it flexibly.",
      },
    };
  }

  initials(name) {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("");
  }

  /**
   * Flattens { category, subCategories: [...] } entries from the chosen
   * report section into a single list of "standard" rows.
   */
  buildStandardsForSubCategories() {
    const { section, standardNames, atRiskMax } = this.props;
    const apiResponse = this.state.apiResponse;
    const categories = (apiResponse && apiResponse[section]) || [];

    // Flatten all subCategories first
    const subCategories = categories.flatMap((cat) => cat.subCategories || []);

    const grouped = {};

    subCategories.forEach((sub) => {
      const code = sub.category || "default";

      // Keep the same name logic as before
      const name = standardNames[code] || code;

      if (!grouped[name]) {
        grouped[name] = {
          code,
          name,
          students: {},
        };
      }

      const group = grouped[name];

      const students = Array.isArray(sub.students) ? sub.students : [];

      students.forEach((student) => {
        const id = student.studentID;

        if (!group.students[id]) {
          group.students[id] = {
            studentID: student.studentID,
            studentName: student.studentName,
            answeredCorrectly: 0,
            answeredIncorrectly: 0,
            totalQuestions: 0,
          };
        }

        const existing = group.students[id];

        existing.answeredCorrectly += student.answeredCorrectly || 0;
        existing.answeredIncorrectly += student.answeredIncorrectly || 0;
        existing.totalQuestions += student.totalQuestions || 0;
      });
    });

    const standards = Object.values(grouped).map((group) => {
      const students = Object.values(group.students);

      // Calculate weighted average for each unique student
      const calculatedStudents = students.map((student) => ({
        ...student,
        average:
          student.totalQuestions > 0
            ? (student.answeredCorrectly / student.totalQuestions) * 100
            : 0,
      }));

      // Calculate weighted average for the merged standard
      const totalCorrect = calculatedStudents.reduce(
        (sum, student) => sum + student.answeredCorrectly,
        0,
      );

      const totalQuestions = calculatedStudents.reduce(
        (sum, student) => sum + student.totalQuestions,
        0,
      );

      const average =
        totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;

      const belowStudents = calculatedStudents
        .filter((student) => student.average <= atRiskMax)
        .sort((a, b) => a.average - b.average);

      // Same returned structure
      return {
        code: group.code,
        name: group.name,
        average: Math.round(average),
        totalStudents: students.length,
        belowStudents,
        totalQuestions,
      };
    });
    return standards;
  }

  buildStandardsWithCategories() {
    const { section, standardNames, atRiskMax } = this.props;
    const apiResponse = this.state.apiResponse;
    const categories = (apiResponse && apiResponse[section]) || [];
    const standards = [];

    categories.forEach((cat) => {
      const subCategories = cat.subCategories || [];
      subCategories.forEach((sub) => {
        const code = sub.category || "default";
        const students = Array.isArray(sub.students) ? sub.students : [];
        const belowStudents = students
          .filter((s) => s.average <= atRiskMax)
          .sort((a, b) => a.average - b.average);

        standards.push({
          code,
          name: standardNames[code] || cat.category || code,
          average: Math.round(sub.average || 0),
          totalStudents: students.length,
          belowStudents,
          totalQuestions: sub.totalQuestions,
        });
      });
    });

    return standards;
  }

  onViewAllStudents(standard) {
    this.setState({
      showAllStudents: true,
      selectedStandard: standard,
    });
  }

  getViewAllStudent() {
    const standard = this.state.selectedStandard;
    let context = this;
    const belowStudents = standard.belowStudents;
    let modalDialog = (
      <Modal
        id="view_all_students"
        show={this.state.showAllStudents}
        onHide={() => {
          this.setState({ showAllStudents: false, selectedStandard: null });
        }}
        container={this}
        aria-labelledby="contained-modal-title"
      >
        <Modal.Header>
          <Modal.Title>
            <div className="sp-pop-hd" style={{ padding: "15px" }}>
              <div className="sp-pop-t">
                Students below {this.props.atRiskMax}%
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="view-all-students-topic">
          {this.state.categoryView && 
            <div>
              <div className="sp-std-code">{standard.name}</div>
              <div className="sp-std-name">{standard.code}</div>
            </div>
          }
          {!this.state.categoryView && <div className="sp-std-name">{standard.name}</div>}
          
          </div>
          <div className="sp-pop-list" style={{ maxHeight: "350px" }}>
            {belowStudents.map((s) => (
              <div className="sp-pop-item" key={s.studentID}>
                <div className="sp-pop-av">{this.initials(s.studentName)}</div>
                <span className="sp-pop-n">{s.studentName}</span>
                <span className="sp-pop-sc">{Math.round(s.average)}%</span>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => {
              this.setState({ showAllStudents: false, selectedStandard: null });
            }}
            bsStyle="primary"
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>
    );
    return modalDialog;
  }

  renderPopover(standard) {
    if (standard.belowStudents.length === 0) return null;

    const isPinned = this.state.pinnedCode === standard.code;
    const isHovered = this.state.hoveredCode === standard.code;
    if (!isPinned && !isHovered) return null;

    const style = isPinned ? this.state.pinnedStyle : this.state.hoveredStyle;
    const belowStudents = standard.belowStudents.slice(
      0,
      this.numberOfStudentsInPopover,
    );
    return (
      <div
        className="sp-popover"
        style={style}
        onMouseEnter={() => {
          if (this._hoverCloseTimeout) {
            clearTimeout(this._hoverCloseTimeout);
            this._hoverCloseTimeout = null;
          }
        }}
        onMouseLeave={() => {
          if (!isPinned) this.handlePopoverClose();
        }}
      >
        <div className="sp-pop-hd">
          <div className="sp-pop-t">Students below {this.props.atRiskMax}%</div>
          <div className="sp-pop-s">{standard.code}</div>
        </div>
        <div className="sp-pop-list">
          {belowStudents.map((s) => (
            <div className="sp-pop-item" key={s.studentID}>
              <div className="sp-pop-av">{this.initials(s.studentName)}</div>
              <span className="sp-pop-n">{s.studentName}</span>
              <span className="sp-pop-sc">{Math.round(s.average)}%</span>
            </div>
          ))}
        </div>
        {belowStudents.length < standard.belowStudents.length && (
          <div className="sp-pop-ft">
            <button
              type="button"
              className="sp-pop-btn"
              onClick={(e) => {
                e.stopPropagation();
                this.onViewAllStudents(standard);
              }}
            >
              View all ({standard.belowStudents.length}) →
            </button>
          </div>
        )}
      </div>
    );
  }

  renderStandardRow(standard) {
    const t = this.tier(standard.average);
    const hasBelow = standard.belowStudents.length > 0;

    return (
      <div className="sp-std-row" key={standard.code}>
        <div>
          {/* <div className="sp-std-code">{standard.code}</div> */}
          {this.state.categoryView && 
            <div>
              <div className="sp-std-code">{standard.name}</div>
              <div className="sp-std-name">{standard.code}</div>
            </div>
          }
          {!this.state.categoryView && <div className="sp-std-name">{standard.name}</div>}
          <div className="sp-bar-track">
            <div
              className={`sp-bar-fill ${t.bc}`}
              style={{ width: `${Math.min(standard.average, 100)}%` }}
            />
            <div
              className="sp-bar-tick"
              style={{ left: `${this.props.atRiskMax + 1}%` }}
            />
            <div
              className="sp-bar-tick"
              style={{ left: `${this.props.approachingMax}%` }}
            />
            <div
              className="sp-bar-tick"
              style={{ left: `${this.props.proficientMax}%` }}
            />
          </div>
        </div>

        <div className="sp-met">
          <span className={`sp-met-v ${t.nc}`}>{standard.average}%</span>
          <span className={`sp-pill ${t.pc}`}>{t.label}</span>
        </div>

        {hasBelow ? (
          <button
            type="button"
            className="sp-stu-cell"
            onMouseEnter={(e) => this.handlePopoverOpen(e, standard)}
            onMouseLeave={this.handlePopoverClose}
            onFocus={(e) => this.handlePopoverOpen(e, standard)}
            onBlur={this.handlePopoverClose}
            onClick={(e) => this.handlePopoverToggle(e, standard)}
          >
            <span className="sp-met-v sp-ar-stu">
              {standard.belowStudents.length}
            </span>
            <span className="sp-met-l">of {standard.totalStudents}</span>
            {this.renderPopover(standard)}
          </button>
        ) : (
          <div className="sp-met">
            <span className="sp-met-v sp-pr-n">0</span>
            <span className="sp-met-l">of {standard.totalStudents}</span>
          </div>
        )}
      </div>
    );
  }

  getAllSubjects() {
    let context = this;
    let schoolName = this.props.auth.user.schoolName
      ? this.props.auth.user.schoolName
      : "trial";
    let params = {
      schoolName: schoolName,
      ownerName: this.props.auth.user.userName,
    };
    let pathTemplate =
      "/subjects/ownerName/{ownerName}/schoolName/{schoolName}";
    let method = "GET";
    let additionalParams = {};
    let body = {};
    apiClient
      .invokeApi(params, pathTemplate, method, additionalParams, body)
      .then(function (result) {
        context.setState({
          subjects: result.data,
        });
      })
      .catch(function (error) {
        context.hideIndicator();
      });
  }

  getSubjectSelections() {
    let subjects = this.state.subjects;
    let nodes = [];
    if (subjects) {
      subjects.forEach((sub) => {
        nodes.push({ id: sub.id, name: sub.name });
      });
    }

    return (
      <div className="sp-subject-selection">
        <SelectFieldGroup
          field="selectedSubject"
          value={this.props.params.subject}
          options={nodes}
          placeholder="Select a Subject"
          onChange={(evt) => {
            let subject = evt.target.value;
            browserHistory.replace("/reports/pulse/" + subject);
          }}
        />
      </div>
    );
  }

  render() {
    const { onFullReport, onSuggestRemediation } = this.props;
    const { filter, expanded, loading, error, showAlert, alertText } =
      this.state;
    const isControlled = !!this.props.apiResponse;

    const alertBanner = showAlert && (
      <div className="sp-error" role="alert">
        {alertText}
      </div>
    );

    if (!isControlled && loading) {
      return (
        <div className="sp-panel">
          <div className="sp-loading">Loading standards pulse…</div>
        </div>
      );
    }

    if (!isControlled && error) {
      return (
        <div className="sp-panel">
          {alertBanner}
          <div className="sp-error">
            {"Couldn't load the standards report."}
          </div>
        </div>
      );
    }

    const standards = this.state.categoryView
      ? this.buildStandardsWithCategories()
      : this.buildStandardsForSubCategories();

    if (standards.length === 0) {
      return (
        <div id="TeacherStudentReport">
          <Heading
            isQuery={false}
            breadcrumbsData={[
              {
                name: "Reports",
                action: () => {
                  browserHistory.push("/reports");
                },
              },
              {
                name: "Standards Pulse",
              },
            ]}
          />
          <div className="sp-panel">
            {alertBanner}
            <div className="sp-panel-hd">
              <div className="sp-panel-title">Standards Pulse</div>
              <div className="sp-panel-subject-selection">
                {this.getSubjectSelections()}
              </div>
            </div>
            <div className="sp-empty">
              <div className="sp-empty-ico">—</div>
              <div className="sp-empty-t">No standards data yet</div>
              <div className="sp-empty-d">
                Assign your first activity to start tracking how students
                perform on each standard.
              </div>
            </div>
          </div>
        </div>
      );
    }
    const sorted = standards.slice().sort((a, b) => a.average - b.average);
    const counts = { all: sorted.length, atrisk: 0, approaching: 0, proficient: 0, mastered: 0 };
    sorted.forEach((s) => {
      counts[this.tier(s.average).key] += 1;
    });

    const filtered =
      filter === "all"
        ? sorted
        : sorted.filter((s) => this.tier(s.average).key === filter);
    const showSections = filter === "all";

    const TIER_ORDER = ["all", "atrisk", "approaching", "proficient", "mastered"];
    const TIER_COLOR = {
      all: "#6B7280",
      atrisk: "#E24B4A",
      approaching: "#EF9F27",
      proficient: "#27AE60",
      mastered: "#1A5E35",
    };
    const TIER_LABEL = {
      all: "All",
      atrisk: "At Risk",
      approaching: "Approaching",
      proficient: "Proficient",
      mastered: "Mastered",
    };
    const TIER_INFO = this.tierInfo();

    return (
      <div id="TeacherStudentReport">
        <Heading
          isQuery={false}
          breadcrumbsData={[
            {
              name: "Reports",
              action: () => {
                browserHistory.push("/reports");
              },
            },
            {
              name: "Standards Pulse",
            },
          ]}
        />
        <div className="sp-panel">
          {alertBanner}
          <div className="sp-panel-hd">
            <div className="sp-panel-title">Standards Pulse</div>
            <div className="sp-panel-subject-selection">
              {this.getSubjectSelections()}
            </div>
            <div className="sp-panel-right">
              <div className="sp-category-toggle">
                <input type="checkbox" id='categoryView' className="css-checkbox" onChange={this.toggleCategoryView} checked={this.state.categoryView}/>
                <label htmlFor='categoryView' className="category_view_label css-label1">Category view</label>
              </div>

              {/* <span className="sp-panel-hint">tap tier to filter</span> */}
              {/* <button
                type="button"
                className="sp-vlink"
                onClick={() => onFullReport && onFullReport()}
              >
                Full report ↗
              </button> */}
            </div>
          </div>

          <div className="sp-sum-strip">
            {TIER_ORDER.map((k) => {
              const info = TIER_INFO[k];
              return (
                <button
                  type="button"
                  key={k}
                  className={`sp-sum-cell${filter === k ? " sp-act" : ""}`}
                  data-tier={k}
                  onClick={() => this.setFilter(k)}
                >
                  <div className="sp-sum-n">
                    <span
                      className="sp-sum-dot"
                      style={{ background: TIER_COLOR[k] }}
                    />
                    {counts[k]}
                  </div>
                  <div className="sp-sum-l">{TIER_LABEL[k]}</div>
                  <div className="sp-sum-range">{info.range}</div>

                  <div className="sp-tier-popover">
                    <div
                      className="sp-tier-popover-bar"
                      style={{ background: TIER_COLOR[k] }}
                    />
                    <div className="sp-tier-popover-body">
                      <div className="sp-tier-popover-hd">
                        <span
                          className="sp-tier-popover-label"
                          style={{ color: TIER_COLOR[k] }}
                        >
                          {TIER_LABEL[k]}
                        </span>
                        <span className="sp-tier-popover-range">{info.range}</span>
                      </div>
                      <div className="sp-tier-popover-desc">{info.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {counts.atrisk > 0 && (
            <div className="sp-pulse-note">
              <i class="ti ti-alert-triangle sp-pulse-note-icon"></i>
              <span>
                {counts.atrisk} standard{counts.atrisk > 1 ? "s" : ""} below{" "}
                {this.props.atRiskMax}% — hover a count to see students.
              </span>
              {/* <button
                type="button"
                onClick={() =>
                  onSuggestRemediation &&
                  onSuggestRemediation(
                    sorted.filter((s) => this.tier(s.average).key === "atrisk"),
                  )
                }
              >
                Suggest remediation →
              </button> */}
            </div>
          )}

          <div className="sp-col-hd">
            <span>Standard</span>
            <span style={{ textAlign: "center" }}>Class avg</span>
            <span style={{ textAlign: "center" }}>
              Below {this.props.atRiskMax}%
            </span>
          </div>

          <div className="sp-rows">
            {(() => {
              const { maxVisibleRows } = this.props;
              const collapsible =
                typeof maxVisibleRows === "number" &&
                maxVisibleRows > 0 &&
                filtered.length > maxVisibleRows;
              const visibleList =
                collapsible && !expanded
                  ? filtered.slice(0, maxVisibleRows)
                  : filtered;
              const hiddenCount = filtered.length - visibleList.length;
              const visibleNeedsAction = visibleList.filter(
                (s) => this.tier(s.average).key === "atrisk",
              );
              const visibleOnTrack = visibleList.filter(
                (s) => this.tier(s.average).key !== "atrisk",
              );

              return (
                <React.Fragment>
                  {showSections ? (
                    <React.Fragment>
                      {visibleNeedsAction.length > 0 && (
                        <div className="sp-sec-div sp-sec-div--danger">
                          <i class="ti ti-alert-triangle"></i> Needs Action
                        </div>
                      )}
                      {visibleNeedsAction.map((s) => this.renderStandardRow(s))}
                      {visibleOnTrack.length > 0 && (
                        <div className="sp-sec-div sp-sec-div--success">
                          <i class="ti ti-check"></i> On Track
                        </div>
                      )}
                      {visibleOnTrack.map((s) => this.renderStandardRow(s))}
                    </React.Fragment>
                  ) : (
                    visibleList.map((s) => this.renderStandardRow(s))
                  )}

                  {collapsible && (
                    <div className="sp-show-more">
                      <button type="button" onClick={this.toggleExpanded}>
                        {expanded
                          ? "▲ Show fewer"
                          : `▼ Show ${hiddenCount} more standard${hiddenCount > 1 ? "s" : ""}`}
                      </button>
                    </div>
                  )}

                  {filtered.length === 0 && (
                    <div className="sp-loading">No standards in this tier.</div>
                  )}
                </React.Fragment>
              );
            })()}
          </div>
          {this.state.showAllStudents && this.getViewAllStudent()}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...state,
  };
}

export default connect(mapStateToProps)(StandardsPulseReport);
// export default StandardsPulseReport;